import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import AppShell from "./shell/AppShell";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { HOME } from "./shell/navConfig";
import StabilityLoginPage from "./pages/StabilityLoginPage";

/* Service worker: keep cache fresh on deploys */
if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    const hadController = Boolean(navigator.serviceWorker.controller);
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      reg.addEventListener("updatefound", () => {
        const incoming = reg.installing;
        if (!incoming) return;
        incoming.addEventListener("statechange", () => {
          if (incoming.state === "installed" && navigator.serviceWorker.controller) incoming.postMessage("SKIP_WAITING");
        });
      });
    }).catch(() => {});
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      // First installation claims this page too. Reloading then aborts an in-flight login.
      if (!reloading && hadController && window.location.pathname !== '/login') {
        reloading = true;
        window.location.reload();
      }
    });
  });
}

/* Phase-1 persona pages */
const ControlTowerPage = lazy(() => import("./pages/ControlTowerPage"));
const OrdersPage = lazy(() => import("./pages/OrdersPage"));
const DemandPage = lazy(() => import("./pages/DemandPage"));
const MatchInboxPage = lazy(() => import("./pages/MatchInboxPage"));
const StorePickListsPage = lazy(() => import("./pages/StorePickListsPage"));
const StockReconcilePage = lazy(() => import("./pages/StockReconcilePage"));
const ProcurementQueuePage = lazy(() => import("./pages/ProcurementQueuePage"));
const StandardsPage = lazy(() => import("./pages/StandardsPage"));
const VarianceReportPage = lazy(() => import("./pages/VarianceReportPage"));
const MyDepartmentPage = lazy(() => import("./pages/MyDepartmentPage"));
const FloorOverviewPage = lazy(() => import("./pages/FloorOverviewPage"));
const CreateWOPage = lazy(() => import("./pages/CreateWOPage"));
const PlannerPage = lazy(() => import("./pages/PlannerPage"));
const CustomersWorkspacePage=lazy(()=>import('./pages/CustomersWorkspacePage'));
const ReadyDispatchWorkspacePage=lazy(()=>import('./pages/ReadyDispatchWorkspacePage'));
const PlanningWorkspacePage=lazy(()=>import('./pages/PlanningWorkspacePage'));
const ProductionWorkspacePage=lazy(()=>import('./pages/ProductionWorkspacePage'));
const MyWorkPage=lazy(()=>import('./pages/MyWorkPage'));
const SimulationPage=lazy(()=>import('./pages/SimulationPage'));
const SimulationStakeholdersPage=lazy(()=>import('./pages/SimulationStakeholdersPage'));
const ActionDeskPage=lazy(()=>import('./pages/ActionDeskPage'));
const MaterialsWorkspacePage=lazy(()=>import('./pages/MaterialsWorkspacePage'));
const PurchasingWorkspacePage=lazy(()=>import('./pages/PurchasingWorkspacePage'));
const PlanningTodayPage = lazy(() => import('./pages/PlanningTodayPage'));
const PlanningOutlookPage = lazy(() => import('./pages/PlanningOutlookPage'));
const PlanningReadinessPage = lazy(() => import('./pages/PlanningReadinessPage'));
const PlanningInputsPage = lazy(() => import('./pages/PlanningInputsPage'));
const PlanningScenarioPage = lazy(() => import('./pages/PlanningScenarioPage'));
const PlanningMaterialsPage = lazy(() => import('./pages/PlanningMaterialsPage'));
const PlanningReviewPage = lazy(() => import('./pages/PlanningReviewPage'));
const PlanningPerformancePage = lazy(() => import('./pages/PlanningPerformancePage'));
/* Existing modules (restyled by the shell; rebuilt in later phases) */
const WorkOrdersCorePage = lazy(() => import("./pages/WorkOrdersCorePage"));
const WorkHistoryPage = lazy(() => import('./pages/WorkHistoryPage'));
const QCCorePage = lazy(() => import("./pages/QCCorePage"));
const ItemsCorePage = lazy(() => import("./pages/ItemsCorePage"));
const BOMsCorePage = lazy(() => import("./pages/BOMsCorePage"));
const InventoryCorePage = lazy(() => import("./pages/InventoryCorePage"));
const StockMovementsPage = lazy(() => import("./pages/StockMovementsPage"));
const RegistersHubPage = lazy(() => import("./pages/RegistersHubPage"));
const PurchaseCorePage = lazy(() => import("./pages/PurchaseCorePage"));
const DispatchCorePage = lazy(() => import("./pages/DispatchCorePage"));
const SalesHubPage = lazy(() => import("./pages/SalesHubPage"));
const ReportsCorePage = lazy(() => import("./pages/ReportsCorePage"));
const ChatCorePage = lazy(() => import("./pages/ChatCorePage"));
const ChatAccessCorePage = lazy(() => import("./pages/ChatAccessCorePage"));
const LMSCorePage = lazy(() => import("./pages/LMSCorePage"));
const UserManagementPage = lazy(() => import("./pages/UserManagementPage").then(m => ({ default: m.UserManagementPage })));
const SettingsPage = lazy(() => import("./pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const CompanySettingsPage = lazy(() => import("./pages/CompanySettingsPage"));
const SerialTraceabilityPage = lazy(() => import("./pages/SerialTraceabilityPage").then(m => ({ default: m.SerialTraceabilityPage })));
const BulkUploadPage = lazy(() => import("./pages/BulkUploadPage").then(m => ({ default: m.BulkUploadPage })));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

const Fallback = () => <div className="empty" style={{ height: "60vh", display: "grid", placeItems: "center", fontFamily: "JetBrains Mono" }}>Loading…</div>;

function ProtectedRoute({ children }) {
  const { loading, user } = useAuth();
  if (loading) return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "#07090E", color: "#64748B", fontFamily: "JetBrains Mono" }} data-testid="stability-auth-loading-screen">AUTH_CHECK...</div>;
  return user ? children : <Navigate to="/login" replace />;
}

const ALL = ["Admin", "Manager", "Planner", "Store", "Purchase Team", "Sales", "Supervisor", "QC Inspector", "Dispatch", "Operator"];
const MGMT = ["Admin", "Manager"];
const PLAN = ["Admin", "Manager", "Planner"];
const FLOOR = ["Admin", "Manager", "Planner", "Supervisor", "Operator"];
/* Path → allowed roles. Unlisted = all authenticated users */
const ROUTE_ROLES = {
  "/control-tower": PLAN,
  '/customers':[...PLAN,'Sales','Dispatch'], '/ready-dispatch':[...MGMT,'Sales','Planner','Dispatch','QC Inspector','Supervisor','Store','Allotter','Dispatcher'],
  '/planning':[...PLAN,'Supervisor','Store','Purchase Team','QC Inspector','Dispatch'], '/production':ALL,
  '/production/my-work':[...FLOOR,'QC Inspector'], '/materials':[...PLAN,'Store','Purchase Team','Supervisor'],
  '/purchasing':[...MGMT,'Purchase Team','Store'], '/sales/demand/grid':[...PLAN,'Sales'],
  "/orders": [...PLAN, "Sales", "Dispatch", "Store", "Purchase Team", "QC Inspector"],
  "/sales/demand": [...PLAN, "Sales"], "/sales/match": [...PLAN, "Sales"], "/sales": [...MGMT, "Sales", "Dispatch"],
  "/planner": PLAN, "/work-orders": [...PLAN, "Operator", "Supervisor"], "/work-orders/new": PLAN, "/standards": [...PLAN, "Supervisor"],
  '/work-history': PLAN,
  '/planner/inputs': PLAN, '/planner/what-if': PLAN, '/planner/review': PLAN,
  '/planner/today': ALL.filter(r=>r!=='Operator'), '/planner/outlook': ALL.filter(r=>r!=='Operator'),
  '/planner/readiness': ALL.filter(r=>r!=='Operator'), '/planner/materials': ALL.filter(r=>r!=='Operator'), '/planner/performance': ALL.filter(r=>r!=='Operator'),
  "/floor": [...FLOOR, "QC Inspector"], "/floor/my": [...FLOOR, "QC Inspector"],
  "/qc": [...MGMT, "QC Inspector"],
  "/store/pick-lists": [...PLAN, "Store", "Supervisor", "Purchase Team"], "/store/reconcile": [...MGMT, "Store"],
  "/stock-movements": [...PLAN, "Purchase Team", "Store"], "/inventory": [...PLAN, "Operator", "Purchase Team", "Store"],
  "/items": [...PLAN, "Operator", "Purchase Team", "Store"], "/boms": PLAN, "/registers": [...PLAN, "Purchase Team", "Store"],
  "/serial-traceability": [...PLAN, "QC Inspector", "Store"], "/bulk-upload": MGMT, "/reports": PLAN, "/reports/variance": [...PLAN, "Supervisor"],
  "/purchase": [...MGMT, "Purchase Team", "Store"], "/purchase/queue": [...PLAN, "Purchase Team", "Store"],
  "/dispatch": [...MGMT, "Sales", "Dispatch"],
  "/users": ["Admin"], "/settings": ["Admin"], "/company-settings": [...MGMT, "Purchase Team"], "/chat": MGMT, "/chat-access": ["Admin"], "/lms": [...PLAN, "Sales"],
  "/profile": ALL,
};

function RoleRoute({ path, children }) {
  const { user } = useAuth();
  const allowed = ROUTE_ROLES[path];
  if (allowed && user?.role && !allowed.includes(user.role)) return <Navigate to={HOME[user.role] || "/profile"} replace />;
  return <Suspense fallback={<Fallback />}>{children}</Suspense>;
}

function Home() {
  const { user } = useAuth();
  return <Navigate to={HOME[user?.role] || "/profile"} replace />;
}

function LoginRedirect() {
  const { user } = useAuth();
  return user ? <Home /> : <StabilityLoginPage />;
}

const R = (path, El) => <Route key={path} path={path} element={<RoleRoute path={path}><El /></RoleRoute>} />;
function WorkflowRedirect({to,tab}){const location=useLocation();const p=new URLSearchParams(location.search);if(tab&&!p.get('tab'))p.set('tab',tab);return <Navigate replace to={`${to}${p.toString()?'?'+p:''}`}/>;}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginRedirect />} />
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route path="/dashboard" element={<Home />} />
        {R("/control-tower", ControlTowerPage)}
        {R('/simulation', SimulationPage)}
        {R('/simulation/stakeholders', SimulationStakeholdersPage)}
        {R('/actions', ActionDeskPage)}
        {R('/customers',CustomersWorkspacePage)}
        {R('/ready-dispatch',ReadyDispatchWorkspacePage)}
        {R('/planning',PlanningWorkspacePage)}
        <Route path="/daily-plan" element={<Navigate replace to="/planning?view=day"/>}/>
        {R('/production',ProductionWorkspacePage)}
        {R('/production/my-work',MyWorkPage)}
        {R('/materials',MaterialsWorkspacePage)}
        {R('/purchasing',PurchasingWorkspacePage)}
        <Route path="/orders" element={<WorkflowRedirect to="/customers"/>}/>
        <Route path="/sales/demand" element={<WorkflowRedirect to="/customers" tab="expected"/>}/>
        {R('/sales/demand/grid',DemandPage)}
        <Route path="/sales/match" element={<WorkflowRedirect to="/customers" tab="lms"/>}/>
        <Route path="/sales" element={<WorkflowRedirect to="/ready-dispatch"/>}/>
        {R("/planner", PlannerPage)}
        <Route path="/planner/today" element={<Navigate replace to="/planning?view=day"/>}/>
        {R('/planner/outlook', PlanningOutlookPage)}
        {R('/planner/readiness', PlanningReadinessPage)}
        {R('/planner/inputs', PlanningInputsPage)}
        {R('/planner/what-if', PlanningScenarioPage)}
        {R('/planner/materials', PlanningMaterialsPage)}
        <Route path="/planner/plans" element={<Navigate replace to="/planning?view=month"/>}/>
        {R('/planner/review', PlanningReviewPage)}
        {R('/planner/performance', PlanningPerformancePage)}
        {R("/work-orders", WorkOrdersCorePage)}
        {R('/work-history',WorkHistoryPage)}
        {R("/work-orders/new", CreateWOPage)}
        {R("/standards", StandardsPage)}
        <Route path="/floor" element={<WorkflowRedirect to="/production"/>}/>
        <Route path="/floor/my" element={<WorkflowRedirect to="/production/my-work"/>}/>
        {R("/qc", QCCorePage)}
        <Route path="/store/pick-lists" element={<WorkflowRedirect to="/materials"/>}/>
        {R("/store/reconcile", StockReconcilePage)}
        {R("/stock-movements", StockMovementsPage)}
        {R("/inventory", InventoryCorePage)}
        {R("/items", ItemsCorePage)}
        {R("/boms", BOMsCorePage)}
        {R("/registers", RegistersHubPage)}
        {R("/serial-traceability", SerialTraceabilityPage)}
        {R("/bulk-upload", BulkUploadPage)}
        {R("/reports", ReportsCorePage)}
        {R("/reports/variance", VarianceReportPage)}
        {R("/purchase", PurchaseCorePage)}
        {R("/purchase/queue", ProcurementQueuePage)}
        <Route path="/dispatch" element={<WorkflowRedirect to="/ready-dispatch"/>}/>
        <Route path="/qc/pdi" element={<WorkflowRedirect to="/qc" tab="pdi"/>}/>
        {R("/users", UserManagementPage)}
        {R("/settings", SettingsPage)}
        {R("/company-settings", CompanySettingsPage)}
        {R("/profile", ProfilePage)}
        {R("/chat", ChatCorePage)}
        {R("/chat-access", ChatAccessCorePage)}
        {R("/lms", LMSCorePage)}
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function StabilityApp() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster theme="dark" position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
